import { Broker_OrderType, TransactionType } from '../proto/generated/order'
import { QuoteType } from '../proto/generated/quick-trade'
import { OrderBook } from '../proto/generated/security'
import store from '../store'
import { limitPriceFromTopOfBook } from './orderBookPrice'
import { clampQuoteValue, resolveLevelPriceDisplay } from './quickTradePriceOffset'
import { roundToNearestMultiple } from './tradepanel'

export type QuickTradeQuoteScalars = {
    topBid: number
    topAsk: number
    bookLtp: number
    snapLtp: number
    volume: number
}

export const readQuickTradeQuoteScalars = (securityKey: string): QuickTradeQuoteScalars => {
    const book = store.getState().securityStore.candleChangeBook?.[securityKey]
    const snap = store.getState().securityStore.snapshots[securityKey]
    return {
        topBid: book?.topBid ?? 0,
        topAsk: book?.topAsk ?? 0,
        bookLtp: book?.ltp ?? 0,
        snapLtp: snap?.lastTradedPrice ?? 0,
        volume: snap?.volume ?? 0,
    }
}

export const parseQuickTradeQuoteSignature = (signature: string): QuickTradeQuoteScalars => {
    const [topBid = '0', topAsk = '0', bookLtp = '0', snapLtp = '0', volume = '0'] = signature.split('\0')
    return {
        topBid: Number(topBid),
        topAsk: Number(topAsk),
        bookLtp: Number(bookLtp),
        snapLtp: Number(snapLtp),
        volume: Number(volume),
    }
}

export const quoteReferenceFromScalars = (
    transactionType: TransactionType,
    quote: QuickTradeQuoteScalars
): number =>
    limitPriceFromTopOfBook(
        transactionType,
        quote.topBid,
        quote.topAsk,
        quote.bookLtp,
        quote.snapLtp
    )

export type QuickTradeEntryPriceParams = {
    quote: QuickTradeQuoteScalars
    orderBook?: OrderBook
    transactionType: TransactionType
    quoteType?: QuoteType
    quoteValue?: number
    tickSize: number
    priceMultiplier: number
}

/** Sync mirror of {@link getOrderPriceFromQuote} for live UI previews (bips / ticks / levels). */
export const computeQuickTradeEntryPriceDisplay = ({
    quote,
    orderBook,
    transactionType,
    quoteType,
    quoteValue,
    tickSize,
    priceMultiplier,
}: QuickTradeEntryPriceParams): number | undefined => {
    const pm = priceMultiplier > 0 ? priceMultiplier : 100
    const ts = tickSize > 0 ? tickSize : 5

    const askP = orderBook?.asks?.[0]?.price ?? quote.topAsk
    const bidP = orderBook?.bids?.[0]?.price ?? quote.topBid
    let midRaw = askP > 0 && bidP > 0 ? (askP + bidP) / 2 : 0
    if (midRaw <= 0) {
        midRaw = limitPriceFromTopOfBook(
            transactionType,
            quote.topBid,
            quote.topAsk,
            quote.bookLtp,
            quote.snapLtp
        )
    }
    let price = roundToNearestMultiple(midRaw, transactionType, false, ts) / pm

    if (quoteType === QuoteType.LEVEL && quoteValue != null) {
        const levelValue = clampQuoteValue(QuoteType.LEVEL, quoteValue)
        const levelPrice = resolveLevelPriceDisplay(
            transactionType,
            levelValue,
            orderBook,
            askP,
            bidP,
            pm
        )
        if (levelPrice != null && levelPrice > 0) {
            return roundToNearestMultiple(levelPrice * pm, TransactionType.BUY, false, ts) / pm
        }
        return undefined
    }

    if (quoteType == null || quoteValue == null || quoteValue === 0) {
        return price > 0 ? price : undefined
    }

    let bufferPrice = 0
    if (quoteType === QuoteType.BIPS) {
        bufferPrice = price * quoteValue * 0.0001
    } else if (quoteType === QuoteType.TICKS) {
        bufferPrice = (quoteValue * ts) / pm
    }

    let resultPrice = price
    if (quoteType === QuoteType.BIPS || quoteType === QuoteType.TICKS) {
        resultPrice =
            transactionType === TransactionType.BUY
                ? Number(price) - bufferPrice
                : Number(price) + bufferPrice
    }

    resultPrice =
        roundToNearestMultiple(resultPrice * pm, TransactionType.BUY, false, ts) / pm
    return resultPrice > 0 ? resultPrice : undefined
}

export type SlTargetDisplayPriceParams = {
    quote: QuickTradeQuoteScalars
    transactionType: TransactionType
    brokerOrderType: Broker_OrderType
    price: number
    priceMultiplier: number
    addEnabled: boolean
    value: number | string
    valueType: string | undefined
    existingLegPrice?: number
}

export const computeStopLossDisplayPrice = (params: SlTargetDisplayPriceParams): number | undefined => {
    const {
        quote,
        transactionType,
        brokerOrderType,
        price,
        priceMultiplier,
        addEnabled,
        value,
        valueType,
        existingLegPrice,
    } = params
    if (!addEnabled || !value || valueType === 'mtm') return undefined
    if (existingLegPrice != null) return existingLegPrice

    const limitOrder = [Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType)
    const quotePx = quoteReferenceFromScalars(transactionType, quote)
    const basePrice = limitOrder ? price : quotePx > 0 ? quotePx : price
    const pm = priceMultiplier || 100
    const sL = valueType === 'points' ? Number(value) : (price / pm) * Number(value) / 100
    return transactionType === TransactionType.BUY ? basePrice / pm - sL : basePrice / pm + sL
}

export type SlTargetDisplayFromBaseParams = {
    basePriceDisplay: number
    transactionType: TransactionType
    kind: 'stopLoss' | 'target'
    value: number | string
    valueType: 'points' | 'percentage'
}

export const computeSlTargetDisplayPriceFromBase = (params: SlTargetDisplayFromBaseParams): number | undefined => {
    const { basePriceDisplay, transactionType, kind, value, valueType } = params
    if (!value || !Number.isFinite(basePriceDisplay)) return undefined

    const offset =
        valueType === 'points'
            ? Number(value)
            : (basePriceDisplay * Number(value)) / 100

    if (kind === 'stopLoss') {
        return transactionType === TransactionType.BUY
            ? basePriceDisplay - offset
            : basePriceDisplay + offset
    }
    return transactionType === TransactionType.BUY
        ? basePriceDisplay + offset
        : basePriceDisplay - offset
}

export const computeTargetDisplayPrice = (params: SlTargetDisplayPriceParams): number | undefined => {
    const {
        quote,
        transactionType,
        brokerOrderType,
        price,
        priceMultiplier,
        addEnabled,
        value,
        valueType,
        existingLegPrice,
    } = params
    if (!addEnabled || !value || valueType === 'mtm') return undefined
    if (existingLegPrice != null) return existingLegPrice

    const limitOrder = [Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType)
    const quotePx = quoteReferenceFromScalars(transactionType, quote)
    const basePrice = limitOrder ? price : quotePx > 0 ? quotePx : price
    const pm = priceMultiplier || 100
    const tg = valueType === 'points' ? Number(value) : (price / pm) * Number(value) / 100
    return transactionType === TransactionType.BUY ? basePrice / pm + tg : basePrice / pm - tg
}
